import { Component, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { DiagnosticProtocolType, ServerExecutionModeType, SpecificationType } from 'app/app-model/enums';
import { ScommFile } from 'app/app-model/legacy-file';
import { NewSpecification } from 'app/app-model/newSpecification';
import { DiagnosticFamilyModel } from 'app/app-model/server-identification/diagnostic-family.model';
import { ServeridentificationExpressionModel, ServerIdentificationModel } from 'app/app-model/server-identification/server-identification.model';
import { Specification } from 'app/app-model/specification';
import { SpecificationVersion } from 'app/app-model/specification-version';
import { SpecificationVersionInfo } from 'app/app-model/specification-version-info';
import { StructureEcuFamily } from 'app/app-model/structure/structure-ecu-family';
import { StructureSpecification } from 'app/app-model/structure/structure-specification';
import { Rbac, RbacGeneration } from 'app/modules/shared/model/rbac';
import { AuthService } from 'app/modules/authentication/services/auth.service';
import { CategoryItemsService } from 'app/app-services/category-items-service';
import { ConfigService } from 'app/app-services/config.service';
import { DiagnosticFamilyService } from 'app/app-services/diagnostic-family.service';
import { DiagnosticServiceProvider } from 'app/app-services/diagnostic.service.service';
import { EcuIdentifierService } from 'app/app-services/ecu-identification.service';
import { EcuResetService } from 'app/app-services/ecu-reset.service';
import { FingerprintService } from 'app/app-services/fingerprint.service';
import { RbacService } from 'app/app-services/rbac-file-service';
import { SecurityAccessService } from 'app/app-services/security-access.service';
import { ServerIdentificationService } from 'app/app-services/server-identification.service';
import { SessionsService } from 'app/app-services/sessions.service';
import { SpecificationService } from 'app/app-services/specification-service';
import { StructureService } from 'app/app-services/structure-service';
import { VsocMonitoringService } from 'app/app-services/vsocMonitoringService';
import { MessageService } from 'app/modules/shared/services/message-service.service';
import { concat, forkJoin, from, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, concatMap, map, mergeMap, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DialogBase } from '../dialog-base';

interface SpecificationGroup {
    label: string;
    specifications: StructureSpecification[];
}

export enum ImportStepStatus {
    Pending = 'pending',
    Executing = 'executing',
    Complete = 'complete',
    Failed = 'failed'
}

interface ImportStep {
    text: string;
    status: ImportStepStatus;
    message?: string;
    execute: (step: ImportStep) => Observable<unknown>;
}

@Component({
    selector: 'app-import-from-global',
    templateUrl: './import-from-global.component.html',
    styleUrls: ['./import-from-global.component.css', '../../styles/popup.css']
})
export class ImportFromGlobalComponent extends DialogBase implements OnInit, OnDestroy {

    @Output()
    modalClosing: EventEmitter<void> = new EventEmitter<void>();

    @Output()
    specificationImported: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('dropdownContainer') dropdownContainer: ElementRef | undefined;

    // Section 1 - Specification to import
    specificationGroups: SpecificationGroup[] = [];
    selectedSpecification: StructureSpecification | undefined;
    targetSpecificationName = '';
    private existingSpecificationNames: string[] = [];
    private existingSpecificationCodes: string[] = [];
    searchText = '';
    dropdownOpen = false;
    preserveIds = false;
    versionMode: 'latest' | 'custom' = 'latest';
    selectedVersion: SpecificationVersion | undefined;

    // Section 2 - Data to import
    importDiagnosticServices = true;
    importServers = true;
    importCategoryItems = true;
    importRbaccFile = true;
    // For project-specific (Application) rbaccs: set when the source rbacc guid already exists on the target.
    rbaccGuidConflict = false;
    conflictingRbaccGuid = '';

    // Section 3 - Feedback
    loading = false;
    statusMessage = '';
    importSteps: ImportStep[] = [];
    importStepStatus = ImportStepStatus;
    importCompleted = false;
    importFailed = false;
    importFailedAtCreation = false;
    restoring = false;

    // The specification created during step 1, reused by the following steps
    private createdSpecification: Specification | undefined;

    private destroy$ = new Subject<void>();

    constructor(private structureService: StructureService,
        private specificationService: SpecificationService,
        private diagnosticServiceProvider: DiagnosticServiceProvider,
        private serverIdentificationService: ServerIdentificationService,
        private diagnosticFamilyService: DiagnosticFamilyService,
        private categoryItemsService: CategoryItemsService,
        private ecuIdentifierService: EcuIdentifierService,
        private sessionsService: SessionsService,
        private securityAccessService: SecurityAccessService,
        private fingerprintService: FingerprintService,
        private ecuResetService: EcuResetService,
        private vsocMonitoringService: VsocMonitoringService,
        private rbacService: RbacService,
        private configService: ConfigService,
        private authService: AuthService,
        private router: Router,
        private messageService: MessageService) {
        super();
    }

    ngOnInit() {
        this.loadSpecifications();
        this.loadExistingSpecificationNames();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get filteredGroups(): SpecificationGroup[] {
        if (!this.searchText) {
            return this.specificationGroups;
        }
        const search = this.searchText.toLowerCase();
        return this.specificationGroups
            .map(group => ({
                label: group.label,
                specifications: group.specifications.filter(spec => spec.name.toLowerCase().includes(search))
            }))
            .filter(group => group.specifications.length > 0);
    }

    get hasSelectedSpecification(): boolean {
        return !!this.selectedSpecification;
    }

    // Importing from global requires at least the System Owner role.
    get canImportFromGlobal(): boolean {
        return this.authService.canCreateDeleteSpecification();
    }

    get availableVersions(): SpecificationVersion[] {
        if (!this.selectedSpecification || !this.selectedSpecification.specificationVersions) {
            return [];
        }
        return [...this.selectedSpecification.specificationVersions]
            .sort((a, b) => b.name.localeCompare(a.name, undefined, { numeric: true }));
    }

    get hasSelectedData(): boolean {
        return this.importDiagnosticServices || this.importServers || this.importCategoryItems || this.importRbaccFile;
    }

    get isTargetNameTooShort(): boolean {
        return !this.targetSpecificationName || this.targetSpecificationName.trim().length < 3;
    }

    get isTargetNameTaken(): boolean {
        if (this.isTargetNameTooShort) {
            return false;
        }
        const name = this.targetSpecificationName.trim().toLowerCase();
        return this.existingSpecificationNames.some(existing => existing.toLowerCase() === name);
    }

    get isTargetNameValid(): boolean {
        return this.hasSelectedSpecification && !this.isTargetNameTooShort && !this.isTargetNameTaken;
    }

    // Preserve IDs only works when the source code is unique on the target instance.
    get isPreserveIdsCodeConflict(): boolean {
        if (!this.preserveIds || !this.selectedSpecification) {
            return false;
        }
        const sourceCode = this.selectedSpecification.code;
        return this.existingSpecificationCodes.some(code => code === sourceCode);
    }

    get conflictingCode(): string {
        return this.selectedSpecification ? this.selectedSpecification.code : '';
    }

    // A Bootloader source imports a default-entity rbacc derived from its servers, so importing
    // servers is mandatory in that case.
    get isSourceBootloader(): boolean {
        return !!this.selectedSpecification
            && this.selectedSpecification.serverExecutionMode === ServerExecutionModeType.Bootloader;
    }

    get isServersRequirementMet(): boolean {
        return !this.isSourceBootloader || this.importServers;
    }

    // A project-specific rbacc can only be imported if its guid does not already exist on the target.
    get hasRbaccGuidConflict(): boolean {
        return this.importRbaccFile && this.rbaccGuidConflict;
    }

    get canImport(): boolean {
        return this.hasSelectedSpecification && this.hasSelectedData && this.hasValidVersionSelection
            && this.isTargetNameValid && !this.isPreserveIdsCodeConflict && this.isServersRequirementMet
            && !this.hasRbaccGuidConflict && this.canImportFromGlobal && !this.loading;
    }

    get hasValidVersionSelection(): boolean {
        return this.versionMode !== 'custom' || !!this.selectedVersion;
    }

    loadSpecifications() {
        this.structureService.getStructure(this.configService.globalApiUrl)
            .pipe(takeUntil(this.destroy$))
            .subscribe(families => {
                this.specificationGroups = this.buildSpecificationGroups(families);
            }, error => {
                this.messageService.dispatchErrorMessageFromApi(error);
            });
    }

    // The target specification names are loaded from the current (target) instance to validate
    // that the imported specification does not clash with an existing name.
    loadExistingSpecificationNames() {
        this.specificationService.getSpecifications()
            .pipe(takeUntil(this.destroy$))
            .subscribe(specifications => {
                this.existingSpecificationNames = specifications.map(specification => specification.name);
                this.existingSpecificationCodes = specifications.map(specification => specification.code);
            }, error => {
                this.messageService.dispatchErrorMessageFromApi(error);
            });
    }

    private buildSpecificationGroups(families: StructureEcuFamily[]): SpecificationGroup[] {
        const groups: SpecificationGroup[] = [];

        families.forEach(family => {
            family.generations.forEach(generation => {
                if (generation.specifications && generation.specifications.length > 0) {
                    groups.push({
                        label: `${family.name} / ${generation.name}`,
                        specifications: [...generation.specifications].sort((a, b) => a.name.localeCompare(b.name))
                    });
                }
            });
        });

        return groups;
    }

    toggleDropdown() {
        this.dropdownOpen = !this.dropdownOpen;
    }

    onSearchChange() {
        this.dropdownOpen = true;
        if (this.selectedSpecification && this.searchText !== this.selectedSpecification.name) {
            this.selectedSpecification = undefined;
            this.targetSpecificationName = '';
            this.versionMode = 'latest';
            this.selectedVersion = undefined;
            this.rbaccGuidConflict = false;
            this.conflictingRbaccGuid = '';
        }
    }

    selectSpecification(spec: StructureSpecification) {
        this.selectedSpecification = spec;
        this.searchText = spec.name;
        this.targetSpecificationName = spec.name;
        this.dropdownOpen = false;
        this.versionMode = 'latest';
        this.selectedVersion = undefined;

        // Importing servers is mandatory for Bootloader specifications.
        if (this.isSourceBootloader) {
            this.importServers = true;
        }

        this.checkRbaccGuidConflict();
    }

    // Project-specific (Application) rbaccs keep their source guid. If that guid already exists on
    // the target instance it cannot be created again, so the import is blocked with a hint.
    private checkRbaccGuidConflict() {
        this.rbaccGuidConflict = false;
        this.conflictingRbaccGuid = '';

        if (!this.selectedSpecification || this.isSourceBootloader) {
            return;
        }

        this.rbacService.getRbacBySpecificationId(this.selectedSpecification.id, this.configService.globalApiUrl)
            .pipe(
                switchMap(sourceRbac => {
                    if (!sourceRbac || !sourceRbac.rbacGuid) {
                        return of(undefined);
                    }
                    const guid = sourceRbac.rbacGuid;
                    return this.rbacExistsOnTargetByGuid(guid).pipe(map(exists => (exists ? guid : undefined)));
                }),
                takeUntil(this.destroy$)
            )
            .subscribe(conflictingGuid => {
                if (conflictingGuid) {
                    this.rbaccGuidConflict = true;
                    this.conflictingRbaccGuid = conflictingGuid;
                }
            }, error => {
                // A missing source rbacc (404) simply means there is nothing to check. Any other
                // error (network, auth, server, ...) must be surfaced instead of being swallowed.
                if (!this.isNotFoundError(error)) {
                    this.messageService.dispatchErrorMessageFromApi(error);
                }
            });
    }

    // Only a "not found" (404) means the rbacc does not exist on the target. Any other error
    // (network, auth, 500, ...) must be propagated instead of being treated as "not found".
    private rbacExistsOnTargetByGuid(rbacGuid: string): Observable<boolean> {
        return this.rbacService.getRbacFromAPI(rbacGuid).pipe(
            map(rbac => !!rbac),
            catchError(error => this.isNotFoundError(error) ? of(false) : throwError(() => error))
        );
    }

    private isNotFoundError(error: unknown): boolean {
        return !!error && (error as { statusCode?: number }).statusCode === 404;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        const target = event.target as Node | null;
        if (this.dropdownOpen && target && this.dropdownContainer &&
            !this.dropdownContainer.nativeElement.contains(target)) {
            this.dropdownOpen = false;
        }
    }

    @HostListener('document:keydown.escape')
    onEscape() {
        if (this.dropdownOpen) {
            this.dropdownOpen = false;
        }
    }

    // Warn the user against refreshing/closing the browser while an import is running.
    @HostListener('window:beforeunload', ['$event'])
    onBeforeUnload(event: BeforeUnloadEvent) {
        if (this.loading) {
            event.preventDefault();
            event.returnValue = '';
        }
    }

    apply() {
        this.import();
    }

    import() {
        if (!this.canImport || !this.selectedSpecification) {
            return;
        }

        this.loading = true;
        this.importCompleted = false;
        this.importFailed = false;
        this.importFailedAtCreation = false;
        this.createdSpecification = undefined;
        this.importSteps = this.buildImportSteps();
        this.runStep(0);
    }

    // Closes the dialog, refreshes the specification tree and navigates to the imported specification.
    finish() {
        const importedSpecification = this.createdSpecification;

        this.specificationImported.emit();
        this.modalClosing.emit();

        if (importedSpecification && importedSpecification.specificationVersions
            && importedSpecification.specificationVersions.length > 0) {
            // Clear the cached category data so the target specification loads fresh.
            this.categoryItemsService.resetCategories();
            this.router.navigate(['specification', importedSpecification.id,
                importedSpecification.specificationVersions[0].id]);
        }

        this.setDefaultValues();
    }

    // Deletes the partially imported specification, then closes the dialog. The dialog is only
    // closed once the deletion has completed, so a failed delete leaves the dialog open (with an
    // error) instead of orphaning a partially-imported specification.
    restoreAndClose() {
        const importedSpecification = this.createdSpecification;

        if (!importedSpecification) {
            this.closeAfterRestore();
            return;
        }

        this.restoring = true;
        this.specificationService.deleteSpecification({ id: importedSpecification.id } as StructureSpecification)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.restoring = false;
                    this.specificationImported.emit();
                    this.closeAfterRestore();
                },
                error: (error) => {
                    this.restoring = false;
                    this.messageService.dispatchErrorMessageFromApi(error);
                }
            });
    }

    private closeAfterRestore() {
        this.modalClosing.emit();
        this.router.navigate(['/content/specifications']);
        this.setDefaultValues();
    }

    get importInProgress(): boolean {
        return this.importSteps.length > 0;
    }

    private buildImportSteps(): ImportStep[] {
        const steps: ImportStep[] = [];

        // Step 1 is always executed and is the base for all following steps.
        steps.push({
            text: 'Create Diagnostic Specification',
            status: ImportStepStatus.Pending,
            execute: () => this.createDiagnosticSpecificationStep()
        });

        if (this.importDiagnosticServices) {
            steps.push({
                text: 'Importing Diagnostic Services',
                status: ImportStepStatus.Pending,
                execute: () => this.importDiagnosticServicesStep()
            });
        }

        if (this.importServers) {
            steps.push({
                text: 'Importing Servers',
                status: ImportStepStatus.Pending,
                execute: () => this.importServersStep()
            });
        }

        if (this.importCategoryItems) {
            steps.push({
                text: 'Importing Category Items',
                status: ImportStepStatus.Pending,
                execute: (step) => this.importCategoryItemsStep(step)
            });
        }

        if (this.importRbaccFile) {
            steps.push({
                text: 'Importing Rbacc File',
                status: ImportStepStatus.Pending,
                execute: (step) => this.importRbaccFileStep(step)
            });
        }

        return steps;
    }

    private runStep(index: number) {
        if (index >= this.importSteps.length) {
            this.loading = false;
            this.importCompleted = true;
            return;
        }

        const step = this.importSteps[index];
        step.status = ImportStepStatus.Executing;

        step.execute(step).subscribe({
            error: (error) => {
                step.status = ImportStepStatus.Failed;
                // Keep a message the step set for itself, otherwise derive one from the error.
                step.message = step.message || this.buildStepFailureMessage(error);
                this.loading = false;
                this.importFailed = true;
                // Step 1 (create specification) is always the first step and is critical.
                this.importFailedAtCreation = index === 0;
                this.messageService.dispatchErrorMessageFromApi(error);
            },
            complete: () => {
                step.status = ImportStepStatus.Complete;
                this.runStep(index + 1);
            }
        });
    }

    private buildStepFailureMessage(error: unknown): string {
        const details = error as { userMessage?: string; detailedMessage?: string; message?: string };
        const reason = details && (details.userMessage || details.detailedMessage || details.message);
        return reason
            ? `Failed: ${reason}. The items were not imported.`
            : 'Failed. The items were not imported.';
    }

    // Step 1 - Create the target specification from the selected source specification.
    private createDiagnosticSpecificationStep(): Observable<Specification> {
        const source = this.selectedSpecification;
        if (!source) {
            return throwError(() => new Error('Source specification could not be determined'));
        }

        const newSpecification = new NewSpecification();
        newSpecification.name = this.targetSpecificationName.trim();
        newSpecification.specificationType = SpecificationType.Upgraded;
        newSpecification.diagnosticProtocol = source.diagnosticProtocol as DiagnosticProtocolType;
        newSpecification.serverExecutionMode = source.serverExecutionMode as ServerExecutionModeType;
        newSpecification.includeInScomm = source.includeInScomm;

        return this.specificationService.createSpecification(newSpecification, undefined as unknown as ScommFile).pipe(
            tap(created => this.createdSpecification = created),
            mergeMap(created => this.applyPreservedIds(created, source))
        );
    }

    // When "Preserve IDs" is enabled, copy the source code and translationId onto the created
    // specification and persist it on the target instance.
    private applyPreservedIds(created: Specification, source: StructureSpecification): Observable<Specification> {
        if (!this.preserveIds) {
            return of(created);
        }

        created.code = source.code;
        created.translationId = source.translationId;

        // The update response may not carry the specification versions, so keep the created
        // specification (which has them) as the reference used by the following steps.
        return this.specificationService.updateSpecification(created).pipe(
            map(() => {
                this.createdSpecification = created;
                return created;
            })
        );
    }

    // Step 2 - Import the diagnostic services of the selected source version into the created specification.
    private importDiagnosticServicesStep(): Observable<unknown> {
        const sourceVersion = this.getSourceVersion();
        const targetVersionId = this.getCreatedVersionId();

        if (!sourceVersion) {
            return throwError(() => new Error('Source version could not be determined'));
        }
        if (targetVersionId == null) {
            return throwError(() => new Error('Target version could not be determined (created specification has no version)'));
        }

        return this.diagnosticServiceProvider.getItemsPreview(sourceVersion.id, this.configService.globalApiUrl).pipe(
            map(items => items.map(item => item.model)),
            map(services => {
                services.forEach(service => this.zeroOutIds(service));
                return services;
            }),
            mergeMap(services => this.diagnosticServiceProvider.postServicesBulk(targetVersionId, services))
        );
    }

    // Step 3 - Import the servers of the selected source version into the created specification.
    private importServersStep(): Observable<unknown> {
        const sourceVersion = this.getSourceVersion();
        const targetVersionId = this.getCreatedVersionId();

        if (!sourceVersion || targetVersionId == null) {
            return throwError(() => new Error('Source version or target version could not be determined'));
        }

        return forkJoin({
            serverItems: this.serverIdentificationService.getItemsPreview(sourceVersion.id, this.configService.globalApiUrl),
            families: this.diagnosticFamilyService.getFamilies()
        }).pipe(
            map(({ serverItems, families }) => {
                const servers = serverItems.map(item => item.model);
                servers.forEach(server => this.prepareServerForImport(server, targetVersionId, families));
                return servers;
            }),
            mergeMap(servers => this.serverIdentificationService.postServersBulk(servers))
        );
    }

    // Maps a source server onto the target instance: zeroing ids, resolving the family/generation
    // ids from the target instance and pointing it to the target specification version.
    private prepareServerForImport(server: ServerIdentificationModel, targetVersionId: number, families: DiagnosticFamilyModel[]) {
        server.id = 0;

        // The source expression is not imported; the API expects a default empty "And" expression.
        server.expression = this.createDefaultServerExpression();
        server.formattedExpression = undefined as unknown as string;

        // The family/generation ids differ between the source and target instances, so resolve
        // them by name from the target instance.
        const matchingFamily = families.find(family => family.name === server.diagnosticFamily);
        server.diagnosticFamilyId = matchingFamily ? matchingFamily.id : 0;

        const matchingGeneration = matchingFamily
            ? matchingFamily.generations.find(generation => generation.name === server.generation)
            : undefined;
        server.generationId = matchingGeneration ? matchingGeneration.id : 0;

        // Point the server to the target specification version.
        const targetVersionInfo = new SpecificationVersionInfo();
        targetVersionInfo.id = targetVersionId;
        server.specificationVersion = targetVersionInfo;
    }

    // Builds a default empty "And" expression, as expected by the API for imported servers.
    private createDefaultServerExpression(): ServeridentificationExpressionModel {
        return {
            id: 0,
            index: 0,
            nodeType: 0,
            expressions: [],
            compareValue: null,
            legacyIdentifierCode: 0,
            compareCanAddress: null,
            compareOperator: 0,
            ecuIdentifierPropertyToCompareWith: null,
            subStringStart: null,
            subStringLength: null,
            searchLength: null
        } as unknown as ServeridentificationExpressionModel;
    }

    // Step 4 - Import the category items of the selected source version into the created specification.
    // Each category is handled individually, in sequence, updating the step message as it progresses.
    private importCategoryItemsStep(step: ImportStep): Observable<unknown> {
        const sourceVersion = this.getSourceVersion();
        const targetVersionId = this.getCreatedVersionId();

        if (!sourceVersion || targetVersionId == null) {
            return throwError(() => new Error('Source version or target version could not be determined'));
        }

        const sourceVersionId = sourceVersion.id;

        const categories: { message: string; run: () => Observable<unknown> }[] = [
            { message: 'Importing Identifications', run: () => this.importIdentifications(sourceVersionId, targetVersionId) },
            { message: 'Importing Sessions', run: () => this.importSessions(sourceVersionId, targetVersionId) },
            { message: 'Importing Security Access', run: () => this.importSecurityAccess(sourceVersionId, targetVersionId) },
            { message: 'Importing Fingerprints', run: () => this.importFingerprints(sourceVersionId, targetVersionId) },
            { message: 'Importing Ecu Resets', run: () => this.importEcuResets(sourceVersionId, targetVersionId) },
            { message: 'Importing VSOC', run: () => this.importVsoc(sourceVersionId, targetVersionId) }
        ];

        return from(categories).pipe(
            concatMap(category => {
                step.message = category.message;
                return category.run();
            }),
            // Clear the per-category status once every category has been imported successfully.
            tap({ complete: () => step.message = undefined })
        );
    }

    // Fetches a single-item category from the source, zeroes ids and inserts every item one by one.
    private importSingleItemCategory<TItem, TModel>(
        items$: Observable<TItem[]>,
        getModel: (item: TItem) => TModel,
        create: (model: TModel) => Observable<unknown>): Observable<unknown> {

        return items$.pipe(
            mergeMap(items => {
                const models = items.map(getModel);
                models.forEach(model => this.zeroOutIds(model));

                if (models.length === 0) {
                    return of(undefined);
                }

                return concat(...models.map(model => create(model)));
            })
        );
    }

    private importIdentifications(sourceVersionId: number, targetVersionId: number): Observable<unknown> {
        return this.importSingleItemCategory(
            this.ecuIdentifierService.getItemsPreview(sourceVersionId, this.configService.globalApiUrl),
            item => item.model,
            model => this.ecuIdentifierService.createEcuIdentifierFromModel(targetVersionId, model));
    }

    private importSessions(sourceVersionId: number, targetVersionId: number): Observable<unknown> {
        return this.importSingleItemCategory(
            this.sessionsService.getItemsPreview(sourceVersionId, this.configService.globalApiUrl),
            item => item.model,
            model => this.sessionsService.createSessionFromModel(targetVersionId, model));
    }

    private importSecurityAccess(sourceVersionId: number, targetVersionId: number): Observable<unknown> {
        return this.importSingleItemCategory(
            this.securityAccessService.getItemsPreview(sourceVersionId, this.configService.globalApiUrl),
            item => item.model,
            model => this.securityAccessService.createSecurityAccessFromModel(targetVersionId, model));
    }

    private importFingerprints(sourceVersionId: number, targetVersionId: number): Observable<unknown> {
        return this.importSingleItemCategory(
            this.fingerprintService.getItemsPreview(sourceVersionId, this.configService.globalApiUrl),
            item => item.model,
            model => this.fingerprintService.createFingerprintFromModel(targetVersionId, model));
    }

    private importEcuResets(sourceVersionId: number, targetVersionId: number): Observable<unknown> {
        return this.importSingleItemCategory(
            this.ecuResetService.getItemsPreview(sourceVersionId, this.configService.globalApiUrl),
            item => item.model,
            model => this.ecuResetService.createEcuResetFromModel(targetVersionId, model));
    }

    // VSOC items are created by default on the target, so they are removed first and then replaced
    // with the source items using the bulk endpoints.
    private importVsoc(sourceVersionId: number, targetVersionId: number): Observable<unknown> {
        return this.vsocMonitoringService.removeItemsBulk(targetVersionId).pipe(
            mergeMap(() => this.vsocMonitoringService.getItemsPreview(sourceVersionId, this.configService.globalApiUrl)),
            map(items => {
                const models = items.map(item => item.model);
                models.forEach(model => this.zeroOutIds(model));
                return models;
            }),
            mergeMap(models => this.vsocMonitoringService.addItemsBulk(targetVersionId, models))
        );
    }

    // Step 5 - Import the rbacc file of the source specification and re-connect it on the target.
    private importRbaccFileStep(step: ImportStep): Observable<unknown> {
        const targetSpecificationId = this.createdSpecification ? this.createdSpecification.id : undefined;

        if (targetSpecificationId == null) {
            return throwError(() => new Error('Target specification could not be determined'));
        }

        // A Bootloader source imports a default-entity rbacc resolved from its servers.
        if (this.isSourceBootloader) {
            return this.importBootloaderRbacc(step);
        }

        return this.importApplicationRbacc(step, targetSpecificationId);
    }

    // Application source: the rbacc is bound to the specification itself (or its own generations).
    private importApplicationRbacc(step: ImportStep, targetSpecificationId: number): Observable<unknown> {
        const sourceSpecificationId = this.selectedSpecification ? this.selectedSpecification.id : undefined;

        if (sourceSpecificationId == null) {
            return throwError(() => new Error('Source specification could not be determined'));
        }

        return this.rbacService.getRbacBySpecificationId(sourceSpecificationId, this.configService.globalApiUrl).pipe(
            mergeMap(sourceRbac => {
                if (!sourceRbac) {
                    step.message = 'No rbacc file was found';
                    return of(undefined);
                }

                // Keep the source connections aside, they are re-created after the rbacc file is posted.
                const sourceGenerations = sourceRbac.connectedGenerations || [];
                const isDefaultEntity = sourceRbac.defaultEntity;

                return this.uploadRbaccToTarget(sourceRbac).pipe(
                    mergeMap(createdRbac =>
                        this.connectRbac(createdRbac, isDefaultEntity, sourceGenerations, targetSpecificationId))
                );
            })
        );
    }

    // Bootloader source: resolve a default-entity rbacc from the family/generation of the first
    // source server and connect it to every family/generation pair present in the servers.
    private importBootloaderRbacc(step: ImportStep): Observable<unknown> {
        const sourceVersion = this.getSourceVersion();

        if (!sourceVersion) {
            return throwError(() => new Error('Source version could not be determined'));
        }

        return this.serverIdentificationService.getItemsPreview(sourceVersion.id, this.configService.globalApiUrl).pipe(
            mergeMap(serverItems => {
                const servers = (serverItems || []).map(item => item.model);

                if (servers.length === 0) {
                    step.message = 'No server available from source, can\'t import default entity rbacc';
                    return of(undefined);
                }

                const familyName = servers[0].diagnosticFamily;
                // Bootloader server generations carry a "_BOOT" suffix (e.g. "ACC1_BOOT") that must
                // be stripped to resolve the actual default-entity generation (e.g. "ACC1").
                const generationName = this.stripBootSuffix(servers[0].generation);

                // Get the default-entity rbacc from the source (global) instance.
                return this.rbacService.getDefaultEntityRbac(familyName, generationName, this.configService.globalApiUrl).pipe(
                    catchError(() => {
                        step.message = `Failed to get default entity for ${familyName}-${generationName}`;
                        return throwError(() => new Error(step.message));
                    }),
                    mergeMap(sourceRbac => this.defaultEntityExistsOnTarget(familyName, generationName).pipe(
                        mergeMap(existsOnTarget => {
                            if (existsOnTarget) {
                                step.message = 'Default entity exists already, skipping';
                                return of(undefined);
                            }

                            // The default-entity lookup can return a lighter object; fetch the full
                            // rbacc by guid so the signature/certificate/fileHash are all present.
                            return this.rbacService.getRbacFromAPI(sourceRbac.rbacGuid, this.configService.globalApiUrl).pipe(
                                mergeMap(fullRbac => this.uploadRbaccToTarget(fullRbac)),
                                mergeMap(createdRbac => this.connectRbaccToServerGenerations(createdRbac, servers))
                            );
                        })
                    ))
                );
            })
        );
    }

    // Strips the "_BOOT" suffix from a bootloader generation name (e.g. "ACC1_BOOT" -> "ACC1").
    private stripBootSuffix(generationName: string): string {
        return generationName ? generationName.replace(/_BOOT$/i, '') : generationName;
    }

    // Checks whether a default-entity rbacc already exists on the target for the family/generation.
    private defaultEntityExistsOnTarget(familyName: string, generationName: string): Observable<boolean> {
        return this.rbacService.getDefaultEntityRbac(familyName, generationName).pipe(
            map(rbac => !!rbac),
            // Only a "not found" (404) means it does not exist; propagate any other error.
            catchError(error => this.isNotFoundError(error) ? of(false) : throwError(() => error))
        );
    }

    // Downloads the source rbacc xml and creates the rbacc file on the target (without connections).
    private uploadRbaccToTarget(sourceRbac: Rbac): Observable<Rbac> {
        // The rbacc metadata does not contain the actual xml file. It has to be downloaded
        // separately so the created rbacc carries the file the hash/signature is validated against.
        // The file must be downloaded as binary (blob) to preserve the exact original bytes;
        // downloading as text would re-encode it and change the recomputed file hash.
        return this.rbacService.getRbaccFileAsBlob(sourceRbac.rbacGuid, this.configService.globalApiUrl).pipe(
            mergeMap(rbaccBlob => this.prepareRbaccFileForUpload(rbaccBlob)),
            map(prepared => this.buildRbacToImport(sourceRbac, prepared.data, prepared.fileHash)),
            mergeMap(rbacToCreate => this.rbacService.createRbacFile(rbacToCreate))
        );
    }

    // Prepares the rbacc file for upload: strips a leading UTF-8 BOM (which breaks the server-side
    // XmlDocument.LoadXml parsing) and recomputes the file hash over the exact bytes that are sent,
    // so it always matches the value the API recomputes.
    private prepareRbaccFileForUpload(blob: Blob): Observable<{ data: string; fileHash: string }> {
        return from(blob.arrayBuffer()).pipe(
            mergeMap(buffer => {
                let bytes = new Uint8Array(buffer);
                if (bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
                    bytes = bytes.slice(3);
                }
                const data = this.bytesToBase64(bytes);
                return from(crypto.subtle.digest('SHA-256', bytes)).pipe(
                    map(hashBuffer => ({ data, fileHash: this.bufferToHex(hashBuffer) }))
                );
            })
        );
    }

    private bytesToBase64(bytes: Uint8Array): string {
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    }

    // Matches the API hash format: SHA-256 as a lowercase hex string.
    private bufferToHex(buffer: ArrayBuffer): string {
        return Array.from(new Uint8Array(buffer))
            .map(byte => byte.toString(16).padStart(2, '0'))
            .join('');
    }

    // Connects a default-entity rbacc to every target generation resolved from the source servers.
    private connectRbaccToServerGenerations(createdRbac: Rbac, servers: ServerIdentificationModel[]): Observable<unknown> {
        return this.diagnosticFamilyService.getFamilies().pipe(
            mergeMap(families => {
                const generationIds = this.resolveTargetGenerationIdsFromServers(servers, families);

                if (generationIds.length === 0) {
                    return of(undefined);
                }

                const connectionRequests = generationIds
                    .map(generationId => this.rbacService.createGenConnection(createdRbac.rbacGuid, generationId));

                return concat(...connectionRequests);
            })
        );
    }

    // Resolves the unique target generation ids for the family/generation pairs found in the servers.
    private resolveTargetGenerationIdsFromServers(servers: ServerIdentificationModel[], families: DiagnosticFamilyModel[]): number[] {
        const generationIds = new Set<number>();

        servers.forEach(server => {
            const matchingFamily = families.find(family => family.name === server.diagnosticFamily);
            // Bootloader server generations carry a "_BOOT" suffix that must be stripped to match
            // the actual target generation (e.g. "ACC1_BOOT" -> "ACC1").
            const generationName = this.stripBootSuffix(server.generation);
            const matchingGeneration = matchingFamily
                ? matchingFamily.generations.find(generation => generation.name === generationName)
                : undefined;
            if (matchingGeneration) {
                generationIds.add(matchingGeneration.id);
            }
        });

        return Array.from(generationIds);
    }

    // Builds a fresh rbacc object for the target instance, carrying the downloaded xml file content.
    private buildRbacToImport(sourceRbac: Rbac, base64Data: string, fileHash: string): Rbac {
        const rbac = new Rbac();
        rbac.name = sourceRbac.name;
        rbac.data = base64Data;
        rbac.defaultEntity = sourceRbac.defaultEntity;
        // The hash is recomputed over the exact bytes being uploaded so it matches the API's check.
        rbac.fileHash = fileHash;
        rbac.signature = sourceRbac.signature;
        rbac.certificate = sourceRbac.certificate;
        rbac.ecuFamily = sourceRbac.ecuFamily;
        rbac.targetGenerations = sourceRbac.targetGenerations;
        rbac.rbaccEcuDiagnosticProtocol = sourceRbac.rbaccEcuDiagnosticProtocol;
        rbac.rbaccEcuDiagnosticSecurityType = sourceRbac.rbaccEcuDiagnosticSecurityType;
        rbac.rbaccEcuType = sourceRbac.rbaccEcuType;
        // Preserve the source rbacc state instead of defaulting to "InWork".
        rbac.rbaccState = sourceRbac.rbaccState;
        // Connections are established separately after the rbacc file is created.
        rbac.connectedSpecificationVersions = [];
        rbac.connectedGenerations = [];
        return rbac;
    }

    // Re-creates the rbacc connections on the target instance depending on the default entity flag.
    private connectRbac(createdRbac: Rbac, isDefaultEntity: boolean,
        sourceGenerations: RbacGeneration[], targetSpecificationId: number): Observable<unknown> {

        // A specification-scoped rbacc file is connected to the newly created specification.
        if (!isDefaultEntity) {
            return this.rbacService.createSpecConnection(createdRbac.rbacGuid, targetSpecificationId);
        }

        // A default rbacc file is connected to the matching generations on the target instance.
        return this.diagnosticFamilyService.getFamilies().pipe(
            mergeMap(families => {
                const connectionRequests = sourceGenerations
                    .map(sourceGeneration => this.resolveTargetGenerationId(sourceGeneration, families))
                    .filter((generationId): generationId is number => generationId != null)
                    .map(generationId => this.rbacService.createGenConnection(createdRbac.rbacGuid, generationId));

                if (connectionRequests.length === 0) {
                    return of(undefined);
                }

                return concat(...connectionRequests);
            })
        );
    }

    private resolveTargetGenerationId(sourceGeneration: RbacGeneration, families: DiagnosticFamilyModel[]): number | undefined {
        const matchingFamily = families.find(family => family.name === sourceGeneration.familyName);
        const matchingGeneration = matchingFamily
            ? matchingFamily.generations.find(generation => generation.name === sourceGeneration.generationName)
            : undefined;
        return matchingGeneration ? matchingGeneration.id : undefined;
    }

    private getSourceVersion(): SpecificationVersion | undefined {
        if (!this.selectedSpecification || !this.selectedSpecification.specificationVersions) {
            return undefined;
        }

        if (this.versionMode === 'custom') {
            return this.selectedVersion;
        }

        const versions = this.selectedSpecification.specificationVersions;
        const releasedVersions = versions.filter(version => version.releaseStatus === 1);
        const pool = releasedVersions.length > 0 ? releasedVersions : versions;

        return [...pool].sort((a, b) => b.name.localeCompare(a.name, undefined, { numeric: true }))[0];
    }

    private getCreatedVersionId(): number | undefined {
        if (this.createdSpecification && this.createdSpecification.specificationVersions
            && this.createdSpecification.specificationVersions.length > 0) {
            return this.createdSpecification.specificationVersions[0].id;
        }
        return undefined;
    }

    private zeroOutIds(value: unknown) {
        if (Array.isArray(value)) {
            value.forEach(item => this.zeroOutIds(item));
            return;
        }

        if (value && typeof value === 'object') {
            Object.keys(value).forEach(key => {
                const propertyValue = (value as Record<string, unknown>)[key];
                if (key.toLowerCase().endsWith('id') && typeof propertyValue === 'number') {
                    (value as Record<string, unknown>)[key] = 0;
                } else {
                    this.zeroOutIds(propertyValue);
                }
            });
        }
    }

    cancel() {
        this.close();
    }

    close() {
        this.modalClosing.emit();
        this.setDefaultValues();
    }

    setDefaultValues() {
        this.selectedSpecification = undefined;
        this.targetSpecificationName = '';
        this.searchText = '';
        this.dropdownOpen = false;
        this.preserveIds = false;
        this.versionMode = 'latest';
        this.selectedVersion = undefined;
        this.importDiagnosticServices = true;
        this.importServers = true;
        this.importCategoryItems = true;
        this.importRbaccFile = true;
        this.rbaccGuidConflict = false;
        this.conflictingRbaccGuid = '';
        this.loading = false;
        this.statusMessage = '';
        this.importSteps = [];
        this.importCompleted = false;
        this.importFailed = false;
        this.importFailedAtCreation = false;
        this.createdSpecification = undefined;
    }
}
